import DashboardLayout from "@/components/DashboardLayout";
import ClarityOneCustomerAccessReadiness from "@/components/clarity-one/ClarityOneCustomerAccessReadiness";
import ProductFindImageWorkflow from "@/components/sales/catalogue/ProductFindImageWorkflow";
import { notifyProductImagePreparation } from "@/lib/productImagePreparationFeedback";
import WarrantyCardPresentation, {
  type WarrantyCardCustomerView,
  type WarrantyCardProductView,
} from "@/components/warranty/WarrantyCardPresentation";
import WarrantyDownloadButton from "@/components/warranty/WarrantyDownloadButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  calculateWarrantyExpiry,
  canAdminDirectIssueWarranty,
  canAdminEditPendingWarranty,
  canIssueWarranty,
  hasDurableWarrantyImage,
  isWarrantyEditableStatus,
  validateWarrantyItemForIssuance,
  WARRANTY_CLARITY_SUPPORT_EXPLANATION,
  WARRANTY_MANUFACTURER_AUTHORITY_STATEMENT,
  WARRANTY_MANDATORY_RIGHTS_NOTE,
  WARRANTY_NON_TRANSFERABILITY_STATEMENT,
} from "@shared/warrantyDomain";
import {
  describeWarrantyImageSource,
  describeWarrantyPeriodSource,
} from "@shared/warrantyEnrichmentProvenance";
import type { WarrantyPeriodEvidence, WarrantyPeriodSource } from "@shared/warrantyPeriodAuthority";
import {
  CalendarDays,
  ChevronLeft,
  Eye,
  ImagePlus,
  Loader2,
  PackagePlus,
  Pencil,
  Send,
  ShieldCheck,
  Trash2,
  Upload,
} from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useParams } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type WarrantyItemData = {
  id: number;
  issued?: number | boolean;
  included: number | boolean;
  brand: string;
  model: string;
  productName: string | null;
  sourceCommercialLineKey: string | null;
  suggestionSource: string | null;
  finish: string | null;
  serialNumber: string | null;
  serialNotApplicable: number | boolean;
  warrantyStartDate: Date | string | null;
  warrantyDurationMonths: number | null;
  warrantyExpiryDate: Date | string | null;
  warrantyPeriodSource: WarrantyPeriodSource | null;
  warrantyPeriodEvidenceJson: WarrantyPeriodEvidence | null;
  manufacturerWarrantyLabel: string | null;
  manufacturerTermsUrl: string | null;
  staffNote: string | null;
  imageUrl: string | null;
  imageAlt: string | null;
  imageStorageKey: string | null;
  imageSourceType: string | null;
  imageSourceUrl: string | null;
  imageSourceDomain: string | null;
};

const WARRANTY_TERMS = {
  claritySupportExplanation: WARRANTY_CLARITY_SUPPORT_EXPLANATION,
  nonTransferability: WARRANTY_NON_TRANSFERABILITY_STATEMENT,
  manufacturerAuthority: WARRANTY_MANUFACTURER_AUTHORITY_STATEMENT,
  mandatoryRightsNote: WARRANTY_MANDATORY_RIGHTS_NOTE,
};

function inputDate(value: Date | string | null | undefined): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString().slice(0, 10) : "";
}

function displayDate(value: Date | string | null | undefined): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return date.toLocaleDateString("en-SG", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function toUtcDate(value: string): Date | null {
  return value ? new Date(`${value}T00:00:00.000Z`) : null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? "").split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error ?? new Error("Could not read image."));
    reader.readAsDataURL(file);
  });
}

function isItemIncluded(item: WarrantyItemData): boolean {
  return Boolean(item.included);
}

function toIssuanceItem(item: WarrantyItemData) {
  return {
    id: item.id,
    included: isItemIncluded(item),
    brand: item.brand,
    model: item.model,
    serialNumber: item.serialNumber,
    serialNotApplicable: Boolean(item.serialNotApplicable),
    warrantyStartDate: item.warrantyStartDate,
    warrantyDurationMonths: item.warrantyDurationMonths,
    warrantyPeriodSource: item.warrantyPeriodSource,
    warrantyExpiryDate: item.warrantyExpiryDate,
    manufacturerWarrantyLabel: item.manufacturerWarrantyLabel,
    imageStorageKey: item.imageStorageKey,
    imageUrl: item.imageUrl,
  };
}

function mapItemsToProductViews(items: WarrantyItemData[]): WarrantyCardProductView[] {
  return items.filter(isItemIncluded).map((item) => ({
    id: item.id,
    brand: item.brand,
    model: item.model,
    productName: item.productName,
    finish: item.finish,
    serialNumber: item.serialNumber,
    serialNotApplicable: Boolean(item.serialNotApplicable),
    warrantyStartDate: item.warrantyStartDate,
    warrantyExpiryDate: item.warrantyExpiryDate,
    warrantyDurationMonths: item.warrantyDurationMonths,
    warrantyPeriodSource: item.warrantyPeriodSource,
    manufacturerWarrantyLabel: item.manufacturerWarrantyLabel,
    manufacturerTermsUrl: item.manufacturerTermsUrl,
    imageUrl: item.imageUrl,
    imageAlt: item.imageAlt,
    coverageStatus: "draft",
    validationWarnings: validateWarrantyItemForIssuance(toIssuanceItem(item)).map(
      (issue) => issue.message,
    ),
  }));
}

export type WarrantyItemEditorHandle = {
  flush: () => Promise<void>;
  hasDirty: () => boolean;
};

type WarrantyItemEditorProps = {
  item: WarrantyItemData;
  editable: boolean;
  adminPendingEdit: boolean;
  onRefresh: () => Promise<unknown>;
};

const WarrantyItemEditor = forwardRef<WarrantyItemEditorHandle, WarrantyItemEditorProps>(
  function WarrantyItemEditor({ item, editable, adminPendingEdit, onRefresh }, ref) {
  const uploadRef = useRef<HTMLInputElement>(null);
  const [findImageOpen, setFindImageOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [uploadConfirm, setUploadConfirm] = useState<{
    file: File;
    previewUrl: string;
  } | null>(null);
  const [form, setForm] = useState({
    included: Boolean(item.included),
    brand: item.brand,
    model: item.model,
    productName: item.productName ?? "",
    finish: item.finish ?? "",
    serialNumber: item.serialNumber ?? "",
    serialNotApplicable: Boolean(item.serialNotApplicable),
    warrantyStartDate: inputDate(item.warrantyStartDate),
    warrantyDurationMonths: item.warrantyDurationMonths?.toString() ?? "",
    manufacturerWarrantyLabel: item.manufacturerWarrantyLabel ?? "",
    manufacturerTermsUrl: item.manufacturerTermsUrl ?? "",
    warrantyPeriodReason: "",
    staffNote: item.staffNote ?? "",
  });
  const formRef = useRef(form);
  const silentRef = useRef(false);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    setForm({
      included: Boolean(item.included),
      brand: item.brand,
      model: item.model,
      productName: item.productName ?? "",
      finish: item.finish ?? "",
      serialNumber: item.serialNumber ?? "",
      serialNotApplicable: Boolean(item.serialNotApplicable),
      warrantyStartDate: inputDate(item.warrantyStartDate),
      warrantyDurationMonths: item.warrantyDurationMonths?.toString() ?? "",
      manufacturerWarrantyLabel: item.manufacturerWarrantyLabel ?? "",
      manufacturerTermsUrl: item.manufacturerTermsUrl ?? "",
      warrantyPeriodReason: "",
      staffNote: item.staffNote ?? "",
    });
    setImageFailed(false);
  }, [item]);

  const withAdminFlag = <T extends Record<string, unknown>>(payload: T) =>
    adminPendingEdit ? { ...payload, adminPendingEdit: true } : payload;

  const updateItem = trpc.warranties.updateItem.useMutation({
    onSuccess: () => {
      if (!silentRef.current) {
        toast.success("Warranty item saved");
      }
      silentRef.current = false;
      void onRefresh();
    },
    onError: (error) => {
      silentRef.current = false;
      toast.error(error.message || "Could not save warranty item");
    },
  });
  const verifyWarranty = trpc.warranties.verifyManufacturerWarranty.useMutation({
    onSuccess: () => {
      toast.success("Manufacturer warranty verification updated");
      void onRefresh();
    },
    onError: (error) => toast.error(error.message || "Could not verify manufacturer warranty"),
  });
  const persistImage = trpc.warranties.persistItemImage.useMutation({
    onSuccess: () => {
      toast.success("Image imported to Clarity storage");
      void onRefresh();
    },
    onError: (error) => toast.error(error.message || "Could not import image"),
  });
  const uploadImage = trpc.warranties.uploadItemImage.useMutation({
    onSuccess: (result) => {
      notifyProductImagePreparation({ processing: result.imageProcessing });
      setUploadConfirm(null);
      void onRefresh();
    },
    onError: (error) => toast.error(error.message || "Could not upload image"),
  });

  const save = (
    patch: Omit<Parameters<typeof updateItem.mutate>[0], "itemId" | "adminPendingEdit">,
  ) => {
    if (!editable) return;
    updateItem.mutate(withAdminFlag({ itemId: item.id, ...patch }));
  };

  const buildDirtyPatch = (): Omit<
    Parameters<typeof updateItem.mutate>[0],
    "itemId" | "adminPendingEdit"
  > => {
    const f = formRef.current;
    const patch: Omit<Parameters<typeof updateItem.mutate>[0], "itemId" | "adminPendingEdit"> = {};

    if (f.included !== Boolean(item.included)) {
      patch.included = f.included;
    }
    if (f.brand.trim() !== item.brand) {
      if (f.brand.trim()) patch.brand = f.brand.trim();
    }
    if (f.model.trim() !== item.model) {
      if (f.model.trim()) patch.model = f.model.trim();
    }
    const productName = f.productName.trim() || null;
    if (productName !== (item.productName ?? null)) {
      patch.productName = productName;
    }
    const finish = f.finish.trim() || null;
    if (finish !== (item.finish ?? null)) {
      patch.finish = finish;
    }
    const serialNumber = f.serialNumber.trim() || null;
    if (serialNumber !== (item.serialNumber ?? null)) {
      patch.serialNumber = serialNumber;
    }
    if (f.serialNotApplicable !== Boolean(item.serialNotApplicable)) {
      patch.serialNotApplicable = f.serialNotApplicable;
    }
    const warrantyStartDate = toUtcDate(f.warrantyStartDate);
    const itemStart = item.warrantyStartDate
      ? item.warrantyStartDate instanceof Date
        ? item.warrantyStartDate
        : new Date(item.warrantyStartDate)
      : null;
    const formStartIso = warrantyStartDate?.toISOString().slice(0, 10) ?? null;
    const itemStartIso =
      itemStart && Number.isFinite(itemStart.getTime())
        ? itemStart.toISOString().slice(0, 10)
        : null;
    if (formStartIso !== itemStartIso) {
      patch.warrantyStartDate = warrantyStartDate;
    }
    const months = Number(f.warrantyDurationMonths);
    const normalizedMonths =
      Number.isInteger(months) && months > 0 && months <= 120 ? months : null;
    if (normalizedMonths !== item.warrantyDurationMonths) {
      patch.warrantyDurationMonths = normalizedMonths;
      const reason = f.warrantyPeriodReason.trim();
      if (reason) patch.warrantyPeriodReason = reason;
    }
    const manufacturerWarrantyLabel = f.manufacturerWarrantyLabel.trim() || null;
    if (manufacturerWarrantyLabel !== (item.manufacturerWarrantyLabel ?? null)) {
      patch.manufacturerWarrantyLabel = manufacturerWarrantyLabel;
    }
    const manufacturerTermsUrl = f.manufacturerTermsUrl.trim() || null;
    if (manufacturerTermsUrl !== (item.manufacturerTermsUrl ?? null)) {
      patch.manufacturerTermsUrl = manufacturerTermsUrl;
    }
    const staffNote = f.staffNote.trim() || null;
    if (staffNote !== (item.staffNote ?? null)) {
      patch.staffNote = staffNote;
    }

    return patch;
  };

  useImperativeHandle(
    ref,
    () => ({
      flush: async () => {
        if (!editable) return;
        const patch = buildDirtyPatch();
        if (Object.keys(patch).length === 0) return;
        silentRef.current = true;
        await updateItem.mutateAsync(withAdminFlag({ itemId: item.id, ...patch }));
      },
      hasDirty: () => Object.keys(buildDirtyPatch()).length > 0,
    }),
    [editable, item, adminPendingEdit, updateItem],
  );

  const calculatedExpiry = (() => {
    const start = toUtcDate(form.warrantyStartDate);
    const months = Number(form.warrantyDurationMonths);
    if (!start || !Number.isInteger(months) || months <= 0) return null;
    return calculateWarrantyExpiry(start, months);
  })();

  const busy = updateItem.isPending || persistImage.isPending || uploadImage.isPending || verifyWarranty.isPending;
  const displayTitle =
    form.productName.trim() || form.model.trim() || form.brand.trim() || "Product";
  const showImageWarning =
    item.staffNote?.includes("could not be copied into Clarity storage") ||
    (form.included &&
      !hasDurableWarrantyImage({
        imageStorageKey: item.imageStorageKey,
        imageUrl: item.imageUrl,
      }));
  const imageWarningMessage =
    item.staffNote ??
    "Product image must be stored in Clarity (FIND IMAGE or UPLOAD IMAGE) before submission.";
  const warrantyProvenance = describeWarrantyPeriodSource(item.warrantyPeriodSource);
  const imageProvenance = describeWarrantyImageSource(item.imageSourceType);

  return (
    <Card className={!form.included ? "opacity-70" : undefined}>
      {showImageWarning ? (
        <div className="border-b border-amber-200 bg-amber-50 px-6 py-3 text-sm text-amber-900">
          {imageWarningMessage}
        </div>
      ) : null}
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">{displayTitle}</CardTitle>
            {item.sourceCommercialLineKey || item.suggestionSource ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {item.sourceCommercialLineKey ? `Line: ${item.sourceCommercialLineKey}` : null}
                {item.sourceCommercialLineKey && item.suggestionSource ? " · " : null}
                {item.suggestionSource ? `Source: ${item.suggestionSource}` : null}
              </p>
            ) : null}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.included}
              disabled={!editable || busy}
              onCheckedChange={(checked) => {
                const included = checked === true;
                setForm((current) => ({ ...current, included }));
                save({ included });
              }}
            />
            Include in warranty
          </label>
        </div>
      </CardHeader>
      <CardContent className="grid gap-6 lg:grid-cols-[14rem_1fr]">
        <div className="space-y-3">
          <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg border bg-muted/30">
            {item.imageUrl && !imageFailed ? (
              <img
                src={item.imageUrl}
                alt={item.imageAlt || `${item.brand} ${item.model}`}
                className="h-full w-full object-contain p-3"
                onError={() => setImageFailed(true)}
              />
            ) : (
              <div className="text-center text-muted-foreground">
                <ImagePlus className="mx-auto mb-2 h-8 w-8" />
                <p className="text-xs">No product image</p>
              </div>
            )}
          </div>
          {editable ? (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => setFindImageOpen(true)}
              >
                <ImagePlus className="mr-1.5 h-3.5 w-3.5" />
                FIND IMAGE
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => uploadRef.current?.click()}
              >
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                UPLOAD IMAGE
              </Button>
              {item.imageUrl ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    save({
                      imageUrl: null,
                      imageStorageKey: null,
                      imageAlt: null,
                      imageSourceType: null,
                      imageSourceUrl: null,
                      imageSourceDomain: null,
                    })
                  }
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  REMOVE IMAGE
                </Button>
              ) : null}
              <input
                ref={uploadRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.currentTarget.value = "";
                  if (!file) return;
                  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
                    toast.error("Choose a JPEG, PNG or WebP image.");
                    return;
                  }
                  const previewUrl = URL.createObjectURL(file);
                  setUploadConfirm({ file, previewUrl });
                }}
              />
            </div>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Product name</Label>
            <Input
              value={form.productName}
              disabled={!editable}
              onChange={(event) =>
                setForm((current) => ({ ...current, productName: event.target.value }))
              }
              onBlur={() =>
                save({ productName: form.productName.trim() || null })
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label>Brand</Label>
            <Input
              value={form.brand}
              disabled={!editable}
              onChange={(event) => setForm((current) => ({ ...current, brand: event.target.value }))}
              onBlur={() => {
                if (form.brand.trim()) save({ brand: form.brand.trim() });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Model</Label>
            <Input
              value={form.model}
              disabled={!editable}
              onChange={(event) => setForm((current) => ({ ...current, model: event.target.value }))}
              onBlur={() => {
                if (form.model.trim()) save({ model: form.model.trim() });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Finish</Label>
            <Input
              value={form.finish}
              disabled={!editable}
              onChange={(event) => setForm((current) => ({ ...current, finish: event.target.value }))}
              onBlur={() => save({ finish: form.finish.trim() || null })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Serial number</Label>
            <Input
              value={form.serialNumber}
              disabled={!editable || form.serialNotApplicable}
              onChange={(event) =>
                setForm((current) => ({ ...current, serialNumber: event.target.value }))
              }
              onBlur={() =>
                save({ serialNumber: form.serialNumber.trim() || null })
              }
            />
            <label className="flex items-center gap-2 pt-1 text-xs text-muted-foreground">
              <Checkbox
                checked={form.serialNotApplicable}
                disabled={!editable}
                onCheckedChange={(checked) => {
                  const serialNotApplicable = checked === true;
                  setForm((current) => ({ ...current, serialNotApplicable }));
                  save({ serialNotApplicable });
                }}
              />
              Serial not applicable
            </label>
          </div>
          <div className="space-y-1.5">
            <Label>Warranty start date</Label>
            <Input
              type="date"
              value={form.warrantyStartDate}
              disabled={!editable}
              onChange={(event) =>
                setForm((current) => ({ ...current, warrantyStartDate: event.target.value }))
              }
              onBlur={() =>
                save({
                  warrantyStartDate: toUtcDate(form.warrantyStartDate),
                })
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label>Duration (months)</Label>
            <Input
              type="number"
              min={1}
              max={120}
              value={form.warrantyDurationMonths}
              disabled={!editable}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  warrantyDurationMonths: event.target.value,
                }))
              }
              onBlur={() => {
                const months = Number(form.warrantyDurationMonths);
                const reason = form.warrantyPeriodReason.trim();
                save({
                  warrantyDurationMonths:
                    Number.isInteger(months) && months > 0 && months <= 120 ? months : null,
                  warrantyPeriodReason: reason || null,
                });
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Calculated expiry</Label>
            <div className="flex h-10 items-center rounded-md border bg-muted/30 px-3 text-sm">
              <CalendarDays className="mr-2 h-4 w-4 text-muted-foreground" />
              {displayDate(calculatedExpiry)}
            </div>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Warranty verification</Label>
            <div className="space-y-2 rounded-md border bg-muted/20 p-3 text-xs">
              <div className="flex flex-wrap gap-2">
                <Badge variant={imageProvenance.needsReview ? "outline" : "secondary"}>
                  Image: {imageProvenance.label}
                </Badge>
                <Badge variant={warrantyProvenance.needsReview ? "outline" : "secondary"}>
                  Warranty: {warrantyProvenance.label}
                </Badge>
              </div>
              {item.warrantyPeriodSource === "manufacturer_research" ? (
                <p>
                  Verified from manufacturer source
                  {item.warrantyPeriodEvidenceJson?.retrievedAt
                    ? ` · ${new Date(item.warrantyPeriodEvidenceJson.retrievedAt).toLocaleDateString("en-SG")}`
                    : ""}
                </p>
              ) : null}
              {item.warrantyPeriodEvidenceJson?.confirmedByName ? (
                <p>
                  Confirmed by {item.warrantyPeriodEvidenceJson.confirmedByName}
                  {item.warrantyPeriodEvidenceJson.confirmedAt
                    ? ` · ${new Date(item.warrantyPeriodEvidenceJson.confirmedAt).toLocaleDateString("en-SG")}`
                    : ""}
                </p>
              ) : null}
              {item.manufacturerTermsUrl ? (
                <a
                  href={item.manufacturerTermsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-primary underline underline-offset-2"
                >
                  {item.warrantyPeriodEvidenceJson?.sourceTitle ?? "Authoritative manufacturer source"}
                </a>
              ) : null}
              {warrantyProvenance.needsReview ? (
                <p className="text-amber-800">
                  Manufacturer warranty is not yet verified. Duration stays blank until staff verify
                  from an official manufacturer source or enter it with a source/reason. Do not treat
                  12 months as a default.
                </p>
              ) : null}
              {editable ? (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={verifyWarranty.isPending}
                    onClick={() => verifyWarranty.mutate({ itemId: item.id })}
                  >
                    Verify manufacturer warranty
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!editable}
                    onClick={() =>
                      document.getElementById(`warranty-reason-${item.id}`)?.focus()
                    }
                  >
                    Enter manually
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={verifyWarranty.isPending}
                    onClick={() => verifyWarranty.mutate({ itemId: item.id, forceRefresh: true })}
                  >
                    Re-verify warranty
                  </Button>
                </div>
              ) : null}
            </div>
            {editable ? (
              <div className="space-y-1.5">
                <Label htmlFor={`warranty-reason-${item.id}`}>
                  Manual entry source or internal reason
                </Label>
                <Input
                  id={`warranty-reason-${item.id}`}
                  value={form.warrantyPeriodReason}
                  placeholder="Required when entering duration manually"
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      warrantyPeriodReason: event.target.value,
                    }))
                  }
                />
              </div>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label>Manufacturer warranty label</Label>
            <Input
              value={form.manufacturerWarrantyLabel}
              disabled={!editable}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  manufacturerWarrantyLabel: event.target.value,
                }))
              }
              onBlur={() =>
                save({
                  manufacturerWarrantyLabel: form.manufacturerWarrantyLabel.trim() || null,
                })
              }
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Manufacturer terms URL</Label>
            <Input
              type="url"
              placeholder="https://..."
              value={form.manufacturerTermsUrl}
              disabled={!editable}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  manufacturerTermsUrl: event.target.value,
                }))
              }
              onBlur={() =>
                save({
                  manufacturerTermsUrl: form.manufacturerTermsUrl.trim() || null,
                })
              }
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Staff note</Label>
            <Textarea
              value={form.staffNote}
              disabled={!editable}
              onChange={(event) =>
                setForm((current) => ({ ...current, staffNote: event.target.value }))
              }
              onBlur={() =>
                save({ staffNote: form.staffNote.trim() || null })
              }
            />
          </div>
        </div>
      </CardContent>

      <ProductFindImageWorkflow
        open={findImageOpen}
        onOpenChange={setFindImageOpen}
        searchContext={{
          manufacturer: form.brand,
          model: form.model,
          displayName: form.finish,
        }}
        existingImageUrl={item.imageUrl}
        onAccept={(imageUrl) =>
          persistImage.mutate(
            withAdminFlag({
              itemId: item.id,
              imageUrl,
              sourceUrl: imageUrl,
              alt: `${form.brand} ${form.model} ${form.finish}`.trim(),
            }),
          )
        }
      />

      <Dialog
        open={!!uploadConfirm}
        onOpenChange={(open) => {
          if (!open) {
            if (uploadConfirm?.previewUrl) URL.revokeObjectURL(uploadConfirm.previewUrl);
            setUploadConfirm(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm image upload</DialogTitle>
            <DialogDescription>
              This image will replace the current product image after you confirm.
            </DialogDescription>
          </DialogHeader>
          {uploadConfirm ? (
            <div className="flex justify-center rounded-lg border bg-muted/30 p-4">
              <img
                src={uploadConfirm.previewUrl}
                alt="Upload preview"
                className="max-h-64 max-w-full object-contain"
              />
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={uploadImage.isPending}
              onClick={() => {
                if (uploadConfirm?.previewUrl) URL.revokeObjectURL(uploadConfirm.previewUrl);
                setUploadConfirm(null);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={uploadImage.isPending || !uploadConfirm}
              onClick={() => {
                if (!uploadConfirm) return;
                void fileToBase64(uploadConfirm.file)
                  .then((base64Data) =>
                    uploadImage.mutateAsync(
                      withAdminFlag({
                        itemId: item.id,
                        fileName: uploadConfirm.file.name,
                        contentType: uploadConfirm.file.type as
                          | "image/jpeg"
                          | "image/png"
                          | "image/webp",
                        base64Data,
                        alt: `${form.brand} ${form.model}`.trim(),
                      }),
                    ),
                  )
                  .then(() => {
                    if (uploadConfirm.previewUrl) URL.revokeObjectURL(uploadConfirm.previewUrl);
                  })
                  .catch((error) =>
                    toast.error(error instanceof Error ? error.message : "Upload failed"),
                  );
              }}
            >
              {uploadImage.isPending ? "Uploading…" : "UPLOAD IMAGE"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
  },
);

export default function WarrantyDraftPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const warrantyCardId = Number.parseInt(id ?? "", 10);
  const validId = Number.isFinite(warrantyCardId) && warrantyCardId > 0;
  const { data, isLoading, error, refetch } = trpc.warranties.get.useQuery(
    { warrantyCardId },
    { enabled: validId },
  );
  const [newItem, setNewItem] = useState({ brand: "", model: "", finish: "" });
  const [showAddItem, setShowAddItem] = useState(false);
  const [changeReason, setChangeReason] = useState("");
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [adminEditMode, setAdminEditMode] = useState(false);
  const [draftBusy, setDraftBusy] = useState(false);
  const [previewSnapshot, setPreviewSnapshot] = useState<{
    products: WarrantyCardProductView[];
    customer: WarrantyCardCustomerView;
  } | null>(null);
  const editorRefs = useRef(new Map<number, WarrantyItemEditorHandle>());

  const flushAllEditors = async () => {
    await Promise.all(Array.from(editorRefs.current.values()).map((handle) => handle.flush()));
  };

  const submitForApproval = trpc.warranties.submitForApproval.useMutation({
    onSuccess: () => {
      toast.success("Submitted for Admin approval");
      void refetch();
    },
    onError: (mutationError) => toast.error(mutationError.message || "Could not submit for approval"),
  });
  const approveAndIssue = trpc.warranties.issue.useMutation({
    onSuccess: () => {
      toast.success("Warranty approved and issued");
      setAdminEditMode(false);
      void refetch();
    },
    onError: (mutationError) => toast.error(mutationError.message || "Could not approve and issue"),
  });
  const requestChanges = trpc.warranties.requestChanges.useMutation({
    onSuccess: () => {
      toast.success("Changes requested");
      setChangeDialogOpen(false);
      setChangeReason("");
      setAdminEditMode(false);
      void refetch();
    },
    onError: (mutationError) => toast.error(mutationError.message || "Could not request changes"),
  });
  const retryNotification = trpc.warranties.retryApprovalNotification.useMutation({
    onSuccess: () => {
      toast.success("Approval notification retry sent");
      void refetch();
    },
    onError: (mutationError) => toast.error(mutationError.message || "Could not retry notification"),
  });
  const addItem = trpc.warranties.addItem.useMutation({
    onSuccess: () => {
      toast.success("Product added");
      setNewItem({ brand: "", model: "", finish: "" });
      setShowAddItem(false);
      void refetch();
    },
    onError: (mutationError) => toast.error(mutationError.message || "Could not add product"),
  });

  const previewProducts = useMemo(
    () => (data ? mapItemsToProductViews(data.items as WarrantyItemData[]) : []),
    [data],
  );

  const previewCustomer = useMemo(
    (): WarrantyCardCustomerView | null =>
      data
        ? {
            name: data.card.customerNameSnapshot ?? "",
            contact: data.card.customerContactSnapshot,
            email: data.card.customerEmailSnapshot,
            maskPII: true,
          }
        : null,
    [data],
  );

  if (!validId || error) {
    return (
      <DashboardLayout>
        <div className="py-16 text-center text-muted-foreground">
          {error?.message || "Invalid warranty draft ID."}
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading || !data) {
    return (
      <DashboardLayout>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const { card, items } = data;
  const typedItems = items as WarrantyItemData[];
  const canAdminPendingEdit = canAdminEditPendingWarranty(user?.role, card.status);
  const isAdminActor = canIssueWarranty(user?.role);
  const staffEditable =
    isWarrantyEditableStatus(card.status) &&
    (user?.role === "sales" || user?.role === "admin");
  const adminEditActive = canAdminPendingEdit && adminEditMode;
  const itemEditable = staffEditable || adminEditActive;
  const showAdminReviewPreview = canAdminPendingEdit && !adminEditMode;
  const showItemEditors = !showAdminReviewPreview;
  const canApprove = canAdminDirectIssueWarranty(user?.role, card.status);
  const showSubmitForApproval = staffEditable && !isAdminActor;
  const canRequestChanges = canAdminPendingEdit;
  const notificationFailed =
    card.status === "pending_approval" &&
    (card.approvalNotificationStatus === "failed" ||
      card.approvalNotificationStatus === "skipped");

  const handleSaveDraft = async () => {
    if (draftBusy) return;
    setDraftBusy(true);
    try {
      await flushAllEditors();
      await refetch();
      toast.success("Draft saved");
    } catch (saveError) {
      toast.error(saveError instanceof Error ? saveError.message : "Could not save draft");
    } finally {
      setDraftBusy(false);
    }
  };

  const handlePreview = async () => {
    if (draftBusy) return;
    setDraftBusy(true);
    try {
      await flushAllEditors();
      const result = await refetch();
      if (result.data) {
        setPreviewSnapshot({
          products: mapItemsToProductViews(result.data.items as WarrantyItemData[]),
          customer: {
            name: result.data.card.customerNameSnapshot ?? "",
            contact: result.data.card.customerContactSnapshot,
            email: result.data.card.customerEmailSnapshot,
            maskPII: true,
          },
        });
        setPreviewOpen(true);
      }
    } catch (previewError) {
      toast.error(previewError instanceof Error ? previewError.message : "Could not open preview");
    } finally {
      setDraftBusy(false);
    }
  };

  const handleApproveWarranty = async () => {
    if (draftBusy || approveAndIssue.isPending) return;
    if (staffEditable) {
      setDraftBusy(true);
      try {
        await flushAllEditors();
        const result = await refetch();
        if (!result.data) return;
        const refetchedItems = result.data.items as WarrantyItemData[];
        const includedItems = refetchedItems.filter(isItemIncluded);
        const issues = includedItems.flatMap((item) =>
          validateWarrantyItemForIssuance(toIssuanceItem(item)),
        );
        if (issues.length > 0) {
          toast.error(
            `Cannot approve — fix the following first:\n${issues.map((issue) => `• ${issue.message}`).join("\n")}`,
          );
          return;
        }
      } catch (approveError) {
        toast.error(
          approveError instanceof Error ? approveError.message : "Could not approve warranty",
        );
        return;
      } finally {
        setDraftBusy(false);
      }
    }
    if (
      window.confirm(
        "Approve and issue this warranty? Included item details become immutable customer records.",
      )
    ) {
      approveAndIssue.mutate({ warrantyCardId: card.id });
    }
  };

  const handleSubmitForApproval = async () => {
    if (draftBusy) return;
    setDraftBusy(true);
    try {
      await flushAllEditors();
      const result = await refetch();
      if (!result.data) return;
      const refetchedItems = result.data.items as WarrantyItemData[];
      const includedItems = refetchedItems.filter(isItemIncluded);
      const issues = includedItems.flatMap((item) =>
        validateWarrantyItemForIssuance(toIssuanceItem(item)),
      );
      if (issues.length > 0) {
        toast.error(
          `Cannot submit — fix the following first:\n${issues.map((issue) => `• ${issue.message}`).join("\n")}`,
        );
        return;
      }
      if (
        window.confirm(
          "Submit this warranty for Admin approval? It will become read-only until reviewed.",
        )
      ) {
        submitForApproval.mutate({ warrantyCardId: card.id });
      }
    } catch (submitError) {
      toast.error(submitError instanceof Error ? submitError.message : "Could not submit for approval");
    } finally {
      setDraftBusy(false);
    }
  };

  const handleBackToPreview = async () => {
    try {
      await flushAllEditors();
      await refetch();
    } catch (backError) {
      toast.error(backError instanceof Error ? backError.message : "Could not save changes");
    }
    setAdminEditMode(false);
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start gap-3">
          <Button type="button" variant="ghost" size="icon" onClick={() => window.history.back()}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">Warranty — {card.customerNameSnapshot}</h1>
              <Badge variant={card.status === "issued" ? "default" : "secondary"}>
                {card.status.replaceAll("_", " ")}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Invoice {card.invoiceNumberSnapshot || "—"} · Proposal {card.proposalRefSnapshot || "—"}
            </p>
            {card.status === "changes_requested" && card.changeRequestReason ? (
              <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                Changes requested: {card.changeRequestReason}
              </p>
            ) : null}
            {card.status === "pending_approval" && !canAdminPendingEdit ? (
              <p className="mt-2 text-sm text-muted-foreground">
                Pending Admin approval — editing is locked.
                {card.approvalNotificationStatus
                  ? ` Notification: ${card.approvalNotificationStatus}${
                      card.approvalNotificationError ? ` (${card.approvalNotificationError})` : ""
                    }.`
                  : ""}
              </p>
            ) : null}
            {adminEditActive ? (
              <p className="mt-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-950">
                ADMIN EDIT MODE — pending approval
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {staffEditable ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={draftBusy}
                  onClick={() => void handleSaveDraft()}
                >
                  {draftBusy ? "Saving…" : "SAVE DRAFT"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={draftBusy}
                  onClick={() => void handlePreview()}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  PREVIEW WARRANTY CARD
                </Button>
                {showSubmitForApproval ? (
                  <Button
                    type="button"
                    disabled={draftBusy || submitForApproval.isPending}
                    onClick={() => void handleSubmitForApproval()}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {submitForApproval.isPending ? "Submitting…" : "SUBMIT FOR APPROVAL"}
                  </Button>
                ) : null}
              </>
            ) : null}
            {showAdminReviewPreview ? (
              <Button type="button" variant="outline" onClick={() => setAdminEditMode(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                EDIT
              </Button>
            ) : null}
            {adminEditActive ? (
              <Button
                type="button"
                variant="outline"
                disabled={draftBusy}
                onClick={() => void handleBackToPreview()}
              >
                BACK TO PREVIEW
              </Button>
            ) : null}
            {notificationFailed ? (
              <Button
                type="button"
                variant="outline"
                disabled={retryNotification.isPending}
                onClick={() => retryNotification.mutate({ warrantyCardId: card.id })}
              >
                {retryNotification.isPending ? "Retrying…" : "Retry Admin Notification"}
              </Button>
            ) : null}
            {canRequestChanges ? (
              <Button type="button" variant="outline" onClick={() => setChangeDialogOpen(true)}>
                Request Changes
              </Button>
            ) : null}
            {canApprove ? (
              <Button
                type="button"
                disabled={draftBusy || approveAndIssue.isPending}
                onClick={() => void handleApproveWarranty()}
              >
                <ShieldCheck className="mr-2 h-4 w-4" />
                {approveAndIssue.isPending
                  ? "Issuing…"
                  : card.status === "pending_approval"
                    ? "APPROVE AND ISSUE"
                    : "APPROVE WARRANTY"}
              </Button>
            ) : null}
          </div>
        </div>

        <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
          <DialogContent
            showCloseButton
            className="top-[2.5vh] left-[50%] flex max-h-[95vh] w-[95vw] max-w-6xl translate-x-[-50%] translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl"
          >
            <DialogHeader className="sr-only">
              <DialogTitle>Warranty card preview</DialogTitle>
              <DialogDescription>
                Customer-facing preview using saved warranty data.
              </DialogDescription>
            </DialogHeader>
            {previewSnapshot ? (
              <div className="min-h-0 flex-1 overflow-y-auto bg-[#F9F7F2]">
                <WarrantyCardPresentation
                  products={previewSnapshot.products}
                  customer={previewSnapshot.customer}
                  mode="draft_preview"
                  showLoginCta={false}
                  showDocumentLogo
                  terms={WARRANTY_TERMS}
                />
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        <Dialog open={changeDialogOpen} onOpenChange={setChangeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request changes</DialogTitle>
              <DialogDescription>
                Explain what the submitting staff member must correct before resubmission.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="Required explanation"
              rows={4}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setChangeDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                disabled={requestChanges.isPending || changeReason.trim().length < 3}
                onClick={() =>
                  requestChanges.mutate({ warrantyCardId: card.id, reason: changeReason.trim() })
                }
              >
                {requestChanges.isPending ? "Sending…" : "Request Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {showAdminReviewPreview && previewCustomer ? (
          <Card className="overflow-hidden border-[#E8E4DC]">
            <CardContent className="p-0">
              <WarrantyCardPresentation
                products={previewProducts}
                customer={previewCustomer}
                mode="admin_review"
                showLoginCta={false}
                showDocumentLogo
                terms={WARRANTY_TERMS}
              />
            </CardContent>
          </Card>
        ) : null}

        {showAdminReviewPreview && card.customerId ? (
          <ClarityOneCustomerAccessReadiness customerId={card.customerId} />
        ) : null}

        {!showAdminReviewPreview ? (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Warranty source</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <dt className="text-muted-foreground">Customer</dt>
                    <dd className="font-medium">{card.customerNameSnapshot}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Contact</dt>
                    <dd className="font-medium">
                      {[card.customerContactSnapshot, card.customerEmailSnapshot].filter(Boolean).join(" · ") || "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Site address</dt>
                    <dd className="font-medium">{card.siteAddressSnapshot || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Salesperson</dt>
                    <dd className="font-medium">{card.salespersonNameSnapshot || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Invoice number</dt>
                    <dd className="font-medium">{card.invoiceNumberSnapshot || "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Invoice date</dt>
                    <dd className="font-medium">{displayDate(card.invoiceDateSnapshot)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Final settlement</dt>
                    <dd className="font-medium">{displayDate(card.finalSettlementDateSnapshot)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Proposal reference</dt>
                    <dd className="font-medium">{card.proposalRefSnapshot || "—"}</dd>
                  </div>
                </dl>
                {card.holdReason ? (
                  <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    On hold: {card.holdReason}
                  </p>
                ) : null}
                {card.reviewReason ? (
                  <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    Review required: {card.reviewReason}
                  </p>
                ) : null}
              </CardContent>
            </Card>

            {card.customerId ? (
              <ClarityOneCustomerAccessReadiness customerId={card.customerId} />
            ) : null}

            {showItemEditors ? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold">Warranty items</h2>
                    <p className="text-sm text-muted-foreground">{items.length} product item(s)</p>
                  </div>
                  {itemEditable ? (
                    <Button type="button" variant="outline" onClick={() => setShowAddItem((open) => !open)}>
                      <PackagePlus className="mr-2 h-4 w-4" />
                      Add Product
                    </Button>
                  ) : null}
                </div>

                {showAddItem ? (
                  <Card>
                    <CardContent className="grid gap-3 pt-6 sm:grid-cols-[1fr_1fr_1fr_auto]">
                      <Input
                        placeholder="Brand"
                        value={newItem.brand}
                        onChange={(event) => setNewItem((current) => ({ ...current, brand: event.target.value }))}
                      />
                      <Input
                        placeholder="Model"
                        value={newItem.model}
                        onChange={(event) => setNewItem((current) => ({ ...current, model: event.target.value }))}
                      />
                      <Input
                        placeholder="Finish (optional)"
                        value={newItem.finish}
                        onChange={(event) => setNewItem((current) => ({ ...current, finish: event.target.value }))}
                      />
                      <Button
                        type="button"
                        disabled={!newItem.brand.trim() || !newItem.model.trim() || addItem.isPending}
                        onClick={() =>
                          addItem.mutate({
                            warrantyCardId: card.id,
                            brand: newItem.brand.trim(),
                            model: newItem.model.trim(),
                            finish: newItem.finish.trim() || null,
                          })
                        }
                      >
                        Add
                      </Button>
                    </CardContent>
                  </Card>
                ) : null}

                <div className="space-y-5">
                  {typedItems.map((item) => (
                    <div key={item.id} className="space-y-3">
                    {["issued", "review_required"].includes(card.status) && item.issued && isItemIncluded(item) ? (
                      <WarrantyDownloadButton itemId={item.id} access="staff" />
                    ) : null}
                    <WarrantyItemEditor
                      key={item.id}
                      ref={(handle) => {
                        if (handle) editorRefs.current.set(item.id, handle);
                        else editorRefs.current.delete(item.id);
                      }}
                      item={item}
                      editable={itemEditable}
                      adminPendingEdit={adminEditActive}
                      onRefresh={refetch}
                    />
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </DashboardLayout>
  );
}
